import { createFileRoute, Link } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { FeatureGate } from "@/components/FeatureGate";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Download,
  Image as ImageIcon,
  Loader2,
  Maximize2,
  Palette,
  Plus,
  Save,
  Sparkles,
  Trash2,
  ZoomIn,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CreativeCanvas } from "@/components/CreativeCanvas";
import {
  FORMAT_DIMS,
  TEMPLATES,
  getTemplate,
  type CreativeData,
  type CreativeFormat,
} from "@/lib/creative-templates";
import { supabase } from "@/integrations/supabase/client";
import { BrandLibraryButton } from "@/components/BrandLibraryPicker";
import { useAuth } from "@/hooks/useAuth";
import { useTrialLimits } from "@/hooks/useTrialLimits";
import { TrialLimitBanner } from "@/components/TrialLimitBanner";
import { toastUndoable } from "@/lib/undoable";
import { usePlanUsage } from "@/hooks/usePlanUsage";
import { usePlanAccess } from "@/hooks/usePlanAccess";
import { UpgradePromptDialog } from "@/components/UpgradePromptDialog";

export const Route = createFileRoute("/_app/creative")({
  component: () => (
    <FeatureGate feature="creative">
      <CreativeBuilderPage />
    </FeatureGate>
  ),
  head: () => ({
    meta: [
      { title: "Creative Builder · AdKavi" },
      { name: "description", content: "Design ad creatives from templates and export to PNG." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type SavedCreative = {
  id: string;
  name: string;
  template_id: string;
  format: CreativeFormat;
  data: CreativeData;
  updated_at: string;
};

const PRESET_PALETTE_DEFS: { bg: string; fg: string; accent: string; key: string }[] = [
  { bg: "#FF6B2C", fg: "#0B0B0F", accent: "#FFFFFF", key: "sunset" },
  { bg: "#0B0B0F", fg: "#FFFFFF", accent: "#FF6B2C", key: "midnight" },
  { bg: "#F4F1EC", fg: "#1A1A1A", accent: "#FF6B2C", key: "cream" },
  { bg: "#1A1442", fg: "#FFFFFF", accent: "#FFD23F", key: "indigo" },
  { bg: "#0E7C66", fg: "#FFFFFF", accent: "#FFD23F", key: "forest" },
  { bg: "#FFE4D6", fg: "#3B1F0F", accent: "#FF6B2C", key: "peach" },
];

// Category tags for template filtering — use keys, not translated labels
const TEMPLATE_CATEGORIES: Record<string, string> = {
  "split-bold": "ads",
  "overlay-hero": "photo",
  "minimal-mono": "minimal",
  "promo-banner": "promo",
  "quote-card": "social",
};
const ALL_CATEGORY_KEYS = ["all", "ads", "photo", "minimal", "promo", "social"] as const;

function CreativeBuilderPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { activeClientId } = useTrialLimits();

  // Translated constants (must be inside component to access t)
  const PRESET_PALETTES = PRESET_PALETTE_DEFS.map((p) => ({
    ...p,
    label: t(`creative.palette.${p.key}`),
  }));
  const CATEGORY_LABELS: Record<string, string> = {
    all: t("creative.cat.all"),
    ads: t("creative.cat.ads"),
    photo: t("creative.cat.photo"),
    minimal: t("creative.cat.minimal"),
    promo: t("creative.cat.promo"),
    social: t("creative.cat.social"),
  };
  const ZOOM_OPTIONS: Array<{ label: string; value: "fit" | number }> = [
    { label: t("creative.zoom.fit"), value: "fit" },
    { label: "50%", value: 0.5 },
    { label: "75%", value: 0.75 },
    { label: "100%", value: 1 },
  ];
  const GUIDE_STEPS = [
    { id: "template", label: t("creative.guide.template") },
    { id: "content", label: t("creative.guide.content") },
    { id: "colors", label: t("creative.guide.colors") },
    { id: "image", label: t("creative.guide.image") },
    { id: "save", label: t("creative.guide.save") },
  ];
  const FORMAT_LABELS: Record<CreativeFormat, string> = {
    square: t("creative.fmt.square"),
    story: t("creative.fmt.story"),
    landscape: t("creative.fmt.landscape"),
  };
  const tplKey = (id: string) => id.replace(/-([a-z])/g, (_: string, c: string) => c.toUpperCase());

  const [templateId, setTemplateId] = useState(TEMPLATES[0].id);
  const [format, setFormat] = useState<CreativeFormat>("square");
  const [data, setData] = useState<CreativeData>(TEMPLATES[0].defaults);
  const [name, setName] = useState(t("creative.defaultName"));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedCreative[]>([]);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [zoom, setZoom] = useState<"fit" | number>("fit");
  const [templateFilter, setTemplateFilter] = useState("all");
  // Track which right-panel sections are open
  const [open, setOpen] = useState({ content: true, colors: true, image: false, format: false, export: true });
  const { reached: usageReached, limit: usageLimit, refresh: refreshUsage } = usePlanUsage();
  const { requiredPlanFor } = usePlanAccess();
  const [planUpgradeOpen, setPlanUpgradeOpen] = useState(false);

  const template = useMemo(() => getTemplate(templateId), [templateId]);
  const canvasRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("creatives")
      .select("id,name,template_id,format,data,updated_at")
      .order("updated_at", { ascending: false })
      .limit(20)
      .then(({ data: rows, error }) => {
        if (error) { toast.error(t("creative.toast.loadFailed"), { description: error.message }); return; }
        setSaved((rows ?? []) as SavedCreative[]);
      });
  }, [user]);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const dims = FORMAT_DIMS[format];

    if (zoom !== "fit") {
      stage.style.setProperty("--c-scale", String(zoom));
      return;
    }

    const PAD = 48;
    const update = () => {
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      if (!w || !h) return;
      const scale = Math.min((w - PAD) / dims.w, (h - PAD) / dims.h);
      stage.style.setProperty("--c-scale", String(Math.max(scale, 0.05)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [format, zoom]);

  const pickTemplate = (id: string) => {
    const tpl = getTemplate(id);
    setTemplateId(id);
    setData((prev) => ({
      ...tpl.defaults,
      headline: prev.headline || tpl.defaults.headline,
      subhead: prev.subhead || tpl.defaults.subhead,
      cta: prev.cta || tpl.defaults.cta,
      brand: prev.brand || tpl.defaults.brand,
      imageUrl: prev.imageUrl || tpl.defaults.imageUrl,
    }));
  };

  const startNew = () => {
    setEditingId(null);
    setLastSaved(null);
    setName(t("creative.defaultName"));
    setTemplateId(TEMPLATES[0].id);
    setFormat("square");
    setData(TEMPLATES[0].defaults);
  };

  const loadCreative = (c: SavedCreative) => {
    setEditingId(c.id);
    setName(c.name);
    setTemplateId(c.template_id);
    setFormat(c.format);
    setData({ ...TEMPLATES[0].defaults, ...c.data });
    setLastSaved(new Date(c.updated_at));
  };

  const handleSave = async () => {
    if (!user) { toast.error(t("common.signInToSave")); return; }
    setSaving(true);
    if (editingId) {
      const prior = saved.find((c) => c.id === editingId);
      const { data: row, error } = await supabase
        .from("creatives")
        .update({ name, template_id: templateId, format, data })
        .eq("id", editingId)
        .select("id,name,template_id,format,data,updated_at")
        .single();
      setSaving(false);
      if (error || !row) { toast.error(t("creative.toast.saveFailed"), { description: error?.message }); return; }
      setSaved((prev) => [row as SavedCreative, ...prev.filter((c) => c.id !== editingId)]);
      setLastSaved(new Date());
      if (!prior) { toast.success(t("creative.toast.updated")); return; }
      toastUndoable(t("creative.toast.updated"), {
        undoLabel: t("common.undo"), undoneLabel: t("common.undone"), undoFailedLabel: t("common.undoFailed"),
        onUndo: async () => {
          const { data: reverted, error: upErr } = await supabase.from("creatives")
            .update({ name: prior.name, template_id: prior.template_id, format: prior.format, data: prior.data })
            .eq("id", prior.id).select("id,name,template_id,format,data,updated_at").single();
          if (upErr || !reverted) throw upErr ?? new Error("revert failed");
          const r = reverted as SavedCreative;
          setSaved((prev) => [r, ...prev.filter((c) => c.id !== r.id)]);
          if (editingId === prior.id) { setName(r.name); setTemplateId(r.template_id); setFormat(r.format); setData(r.data); }
        },
      });
    } else {
      if (usageReached("creatives")) { setSaving(false); setPlanUpgradeOpen(true); return; }
      const { data: row, error } = await supabase.from("creatives").insert({
        user_id: user.id, client_id: activeClientId ?? null, name, template_id: templateId, format, data,
      }).select("id,name,template_id,format,data,updated_at").single();
      setSaving(false);
      if (error || !row) { toast.error(t("creative.toast.saveFailed"), { description: error?.message }); return; }
      const inserted = row as SavedCreative;
      setEditingId(inserted.id);
      setSaved((prev) => [inserted, ...prev]);
      setLastSaved(new Date());
      void refreshUsage();
      toastUndoable(t("creative.toast.saved"), {
        undoLabel: t("common.undo"), undoneLabel: t("common.undone"), undoFailedLabel: t("common.undoFailed"),
        onUndo: async () => {
          const { error: delErr } = await supabase.from("creatives").delete().eq("id", inserted.id);
          if (delErr) throw delErr;
          setSaved((prev) => prev.filter((c) => c.id !== inserted.id));
          setEditingId((curr) => (curr === inserted.id ? null : curr));
        },
      });
    }
  };

  const handleDelete = async (id: string) => {
    const removed = saved.find((c) => c.id === id);
    const { error } = await supabase.from("creatives").delete().eq("id", id);
    if (error) { toast.error(t("creative.toast.deleteFailed"), { description: error.message }); return; }
    setSaved((prev) => prev.filter((c) => c.id !== id));
    if (editingId === id) startNew();
    if (!removed || !user) { toast.success(t("creative.toast.deleted")); return; }
    toastUndoable(t("creative.toast.deleted"), {
      undoLabel: t("common.undo"), undoneLabel: t("common.undone"), undoFailedLabel: t("common.undoFailed"),
      onUndo: async () => {
        const { data: row, error: insErr } = await supabase.from("creatives").insert({
          user_id: user.id, client_id: activeClientId ?? null,
          name: removed.name, template_id: removed.template_id, format: removed.format, data: removed.data,
        }).select("id,name,template_id,format,data,updated_at").single();
        if (insErr || !row) throw insErr ?? new Error("restore failed");
        setSaved((prev) => [row as SavedCreative, ...prev]);
      },
    });
  };

  const handleExport = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(canvasRef.current, { cacheBust: true, pixelRatio: 1, skipFonts: true });
      const link = document.createElement("a");
      link.download = `${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${format}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(t("creative.toast.exportedPng"));
    } catch (e) {
      toast.error(t("creative.toast.exportFailed"), { description: e instanceof Error ? e.message : "Try a different image URL" });
    } finally {
      setExporting(false);
    }
  };

  const setField = <K extends keyof CreativeData>(key: K, value: CreativeData[K]) =>
    setData((d) => ({ ...d, [key]: value }));

  const toggleSection = (key: keyof typeof open) =>
    setOpen((s) => ({ ...s, [key]: !s[key] }));

  // Guided checklist completion
  const guideState = {
    template: true,
    content: !!(data.headline.trim() || data.brand.trim()),
    colors: PRESET_PALETTES.some((p) => p.bg === data.bg && p.accent === data.accent) || data.bg !== TEMPLATES[0].defaults.bg,
    image: !!data.imageUrl,
    save: !!editingId,
  };
  const guideStepKeys = ["template", "content", "colors", "image", "save"] as const;
  const guideDoneCount = guideStepKeys.filter((k) => guideState[k]).length;

  const filteredTemplates = TEMPLATES.filter(
    (tpl) => templateFilter === "all" || TEMPLATE_CATEGORIES[tpl.id] === templateFilter,
  );

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 60px)" }}>
      {/* ── TOP BAR ── */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-card shrink-0 flex-wrap">
        {/* Left: Logo + name */}
        <div className="flex items-center gap-2 mr-2">
          <Palette className="w-4 h-4 text-orange-400 shrink-0" />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-8 text-sm w-44 border-transparent bg-transparent hover:border-border focus:border-border focus:bg-background transition-colors"
          />
        </div>

        {/* Center: Format tabs */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
          {(Object.keys(FORMAT_DIMS) as CreativeFormat[]).map((f) => (
            <button
              key={f}
              onClick={() => setFormat(f)}
              className={cn(
                "px-3 py-1 rounded-md text-xs font-medium transition",
                format === f ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {FORMAT_LABELS[f]}
            </button>
          ))}
        </div>

        <Badge variant="outline" className="text-[10px] uppercase tracking-wider hidden sm:flex">
          {FORMAT_DIMS[format].w} × {FORMAT_DIMS[format].h}
        </Badge>

        {/* Zoom controls */}
        <div className="flex items-center gap-1 ml-1">
          <ZoomIn className="w-3.5 h-3.5 text-muted-foreground" />
          <div className="flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
            {ZOOM_OPTIONS.map((z) => (
              <button
                key={String(z.value)}
                onClick={() => setZoom(z.value)}
                className={cn(
                  "px-2.5 py-1 rounded-md text-xs font-medium transition",
                  zoom === z.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {z.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1" />

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          <Link
            to="/ai-studio"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> <Sparkles className="w-3 h-3 text-orange-400" /> AI Studio
          </Link>
          <Button variant="outline" size="sm" onClick={startNew} className="h-8 rounded-full text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> {t("creative.btn.new")}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="h-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            {t("creative.btn.saveShort")}
          </Button>
          <Button size="sm" onClick={handleExport} disabled={exporting} variant="outline" className="h-8 rounded-full text-xs">
            {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
            {t("creative.btn.exportPng")}
          </Button>
        </div>
      </div>

      <TrialLimitBanner kind="post" />

      {/* ── 3-COLUMN EDITOR ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── LEFT: Templates ── */}
        <aside className="w-52 border-r border-border overflow-y-auto bg-card/60 shrink-0 flex flex-col">
          {/* Category filter */}
          <div className="p-3 border-b border-border sticky top-0 bg-card/95 backdrop-blur-sm z-10">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">{t("creative.section.templates")}</p>
            <div className="flex flex-wrap gap-1">
              {ALL_CATEGORY_KEYS.map((catKey) => (
                <button
                  key={catKey}
                  onClick={() => setTemplateFilter(catKey)}
                  className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-medium transition",
                    templateFilter === catKey
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:text-foreground",
                  )}
                >
                  {CATEGORY_LABELS[catKey]}
                </button>
              ))}
            </div>
          </div>

          {/* Template cards */}
          <div className="p-2 space-y-2 flex-1">
            {filteredTemplates.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8 px-2">{t("creative.tpl.empty")}</p>
            ) : (
              filteredTemplates.map((tpl) => (
                <button
                  key={tpl.id}
                  onClick={() => pickTemplate(tpl.id)}
                  className={cn(
                    "w-full text-left rounded-xl border overflow-hidden transition hover:border-primary/60 group",
                    templateId === tpl.id ? "border-primary ring-1 ring-primary/30" : "border-border",
                  )}
                >
                  {/* Color thumbnail */}
                  <div
                    className="h-16 w-full relative flex items-end"
                    style={{ background: tpl.defaults.bg }}
                  >
                    <div className="absolute inset-x-0 top-0 h-full opacity-60"
                      style={{ background: `linear-gradient(135deg, ${tpl.defaults.bg} 40%, ${tpl.defaults.accent}55)` }} />
                    <div className="relative px-2 pb-1.5 pt-4 w-full" style={{ background: `linear-gradient(to top, ${tpl.defaults.bg}ee, transparent)` }}>
                      <div className="text-[9px] font-bold leading-tight truncate" style={{ color: tpl.defaults.fg }}>
                        {tpl.defaults.headline}
                      </div>
                    </div>
                    {templateId === tpl.id && (
                      <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-primary-foreground" />
                      </div>
                    )}
                    <div className="absolute top-1.5 left-1.5">
                      <span className="text-[8px] font-medium px-1.5 py-0.5 rounded-full"
                        style={{ background: tpl.defaults.accent, color: tpl.defaults.bg }}>
                        {CATEGORY_LABELS[TEMPLATE_CATEGORIES[tpl.id]] ?? TEMPLATE_CATEGORIES[tpl.id]}
                      </span>
                    </div>
                  </div>
                  {/* Info */}
                  <div className="px-2.5 py-2 bg-card">
                    <div className="font-semibold text-xs">{t(`creative.tpl.${tplKey(tpl.id)}.name`)}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{t(`creative.tpl.${tplKey(tpl.id)}.desc`)}</div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Saved creatives */}
          {saved.length > 0 && (
            <div className="border-t border-border p-2 space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-1 py-1">{t("creative.section.recent")}</p>
              {saved.map((c) => (
                <div
                  key={c.id}
                  onClick={() => loadCreative(c)}
                  className={cn(
                    "group flex items-center justify-between rounded-lg border px-2.5 py-2 text-xs transition hover:border-primary/40 cursor-pointer",
                    editingId === c.id ? "border-primary bg-primary/5" : "border-border bg-card",
                  )}
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{c.name}</div>
                    <div className="text-[10px] text-muted-foreground">{FORMAT_LABELS[c.format]}</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition p-0.5 ml-1 shrink-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* ── CENTER: Canvas ── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-[#0d0f14]">
          {/* Canvas viewport */}
          <div
            ref={stageRef}
            className="flex-1 flex items-center justify-center"
            style={{
              overflow: zoom === "fit" ? "hidden" : "auto",
              backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          >
            {/* Shadow ring so canvas boundaries are unmistakable */}
            <div className="relative" style={{ filter: "drop-shadow(0 0 0 1px rgba(255,255,255,0.08)) drop-shadow(0 8px 32px rgba(0,0,0,0.6))" }}>
              <CreativeCanvas ref={canvasRef} template={template} format={format} data={data} />
            </div>
          </div>

          {/* Zoom status bar */}
          <div className="border-t border-white/5 px-4 py-1.5 flex items-center gap-4 text-[11px] text-white/40 bg-[#0d0f14] shrink-0">
            <Maximize2 className="w-3 h-3" />
            <span>{FORMAT_LABELS[format]} — {FORMAT_DIMS[format].w} × {FORMAT_DIMS[format].h}px</span>
            <span className="ml-auto">
              {zoom === "fit" ? t("creative.zoom.fitScreen") : t("creative.zoom.pct", { pct: String(Math.round((zoom as number) * 100)) })}
            </span>
            {lastSaved && (
              <span className="text-white/30">{t("creative.savedAt", { time: lastSaved.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}</span>
            )}
          </div>
        </div>

        {/* ── RIGHT: Editor ── */}
        <aside className="w-72 border-l border-border overflow-y-auto bg-card/60 shrink-0">

          {/* Guided checklist */}
          <div className="px-4 pt-4 pb-3 border-b border-border">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{t("creative.section.guide")}</p>
              <span className="text-[10px] text-muted-foreground">{guideDoneCount}/{GUIDE_STEPS.length}</span>
            </div>
            <div className="space-y-1.5">
              {GUIDE_STEPS.map((step, i) => {
                const key = guideStepKeys[i];
                const done = guideState[key];
                return (
                  <div key={step.id} className={cn("flex items-center gap-2 text-xs", done ? "text-foreground" : "text-muted-foreground")}>
                    <div className={cn("w-4 h-4 rounded-full flex items-center justify-center shrink-0 text-[9px] font-bold border",
                      done ? "bg-primary border-primary text-primary-foreground" : "border-border")}>
                      {done ? <Check className="w-2.5 h-2.5" /> : i + 1}
                    </div>
                    {step.label}
                  </div>
                );
              })}
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-1 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(guideDoneCount / GUIDE_STEPS.length) * 100}%` }} />
            </div>
          </div>

          {/* Content section */}
          <EditorSection
            label={t("creative.section.content")}
            open={open.content}
            onToggle={() => toggleSection("content")}
          >
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">{t("creative.field.name")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">{t("creative.field.brand")}</Label>
                <Input value={data.brand} onChange={(e) => setField("brand", e.target.value)} className="h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">{t("creative.field.headline")}</Label>
                <Textarea rows={2} value={data.headline} onChange={(e) => setField("headline", e.target.value)} className="text-xs resize-none" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">{t("creative.field.subheadline")}</Label>
                <Textarea rows={2} value={data.subhead} onChange={(e) => setField("subhead", e.target.value)} className="text-xs resize-none" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">{t("creative.field.cta")}</Label>
                <Input value={data.cta} onChange={(e) => setField("cta", e.target.value)} className="h-8 text-xs" />
              </div>
            </div>
          </EditorSection>

          {/* Colors section */}
          <EditorSection
            label={t("creative.section.colors")}
            open={open.colors}
            onToggle={() => toggleSection("colors")}
          >
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-1.5">
                {PRESET_PALETTES.map((p) => {
                  const active = data.bg === p.bg && data.fg === p.fg && data.accent === p.accent;
                  return (
                    <button
                      key={p.label}
                      onClick={() => setData((d) => ({ ...d, bg: p.bg, fg: p.fg, accent: p.accent }))}
                      className={cn("rounded-lg border p-1.5 text-left transition", active ? "border-primary ring-1 ring-primary/30" : "border-border hover:border-primary/40")}
                      title={p.label}
                    >
                      <div className="flex gap-0.5 mb-1">
                        <span className="w-4 h-4 rounded-sm border border-border/30" style={{ background: p.bg }} />
                        <span className="w-4 h-4 rounded-sm border border-border/30" style={{ background: p.fg }} />
                        <span className="w-4 h-4 rounded-sm border border-border/30" style={{ background: p.accent }} />
                      </div>
                      <div className="text-[9px] font-medium leading-none">{p.label}</div>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <ColorField label={t("creative.field.bg")} value={data.bg} onChange={(v) => setField("bg", v)} />
                <ColorField label={t("creative.field.text")} value={data.fg} onChange={(v) => setField("fg", v)} />
                <ColorField label={t("creative.field.accent")} value={data.accent} onChange={(v) => setField("accent", v)} />
              </div>
            </div>
          </EditorSection>

          {/* Image section */}
          <EditorSection
            label={t("creative.section.image")}
            icon={<ImageIcon className="w-3 h-3" />}
            open={open.image}
            onToggle={() => toggleSection("image")}
          >
            <div className="space-y-2">
              {data.imageUrl ? (
                <div className="relative rounded-xl overflow-hidden border border-border">
                  <img src={data.imageUrl} alt="Creative image" className="w-full max-h-28 object-cover" />
                  <button
                    type="button"
                    onClick={() => setField("imageUrl", "")}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-background/80 border border-border flex items-center justify-center hover:bg-destructive/20 text-xs"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border p-4 text-center">
                  <ImageIcon className="w-6 h-6 text-muted-foreground/30 mx-auto mb-1" />
                  <p className="text-[10px] text-muted-foreground">{t("creative.img.empty")}</p>
                  <p className="text-[10px] text-muted-foreground">{t("creative.img.emptyHint")}</p>
                </div>
              )}
              <BrandLibraryButton
                onSelect={(url) => setField("imageUrl", url)}
                label={t("creative.btn.chooseBrandLib")}
              />
              <Input
                placeholder={t("creative.img.pasteUrl")}
                value={data.imageUrl}
                onChange={(e) => setField("imageUrl", e.target.value)}
                className="h-8 text-xs"
              />
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                {t("creative.img.corsHint")}
              </p>
            </div>
          </EditorSection>

          {/* Format section */}
          <EditorSection
            label={t("creative.section.format")}
            open={open.format}
            onToggle={() => toggleSection("format")}
          >
            <div className="space-y-2">
              {(Object.keys(FORMAT_DIMS) as CreativeFormat[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={cn(
                    "w-full flex items-center justify-between rounded-lg border px-3 py-2 text-xs transition",
                    format === f ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:border-primary/40",
                  )}
                >
                  <span className="font-medium">{FORMAT_LABELS[f]}</span>
                  <span className="text-[10px] opacity-60">{FORMAT_DIMS[f].w}×{FORMAT_DIMS[f].h}</span>
                </button>
              ))}
            </div>
          </EditorSection>

          {/* Export section */}
          <EditorSection
            label={t("creative.section.export")}
            open={open.export}
            onToggle={() => toggleSection("export")}
          >
            <div className="space-y-2">
              <Button onClick={handleSave} disabled={saving} className="w-full h-8 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs">
                {saving ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Save className="w-3 h-3 mr-1.5" />}
                {editingId ? t("creative.btn.update") : t("creative.btn.save")}
              </Button>
              <Button onClick={handleExport} disabled={exporting} variant="outline" className="w-full h-8 rounded-full text-xs">
                {exporting ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Download className="w-3 h-3 mr-1.5" />}
                {t("creative.btn.exportPngFmt", { w: String(FORMAT_DIMS[format].w), h: String(FORMAT_DIMS[format].h) })}
              </Button>
              {lastSaved && (
                <p className="text-[10px] text-muted-foreground text-center">
                  {t("creative.lastSavedAt", { time: lastSaved.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}
                </p>
              )}
              {!lastSaved && editingId && (
                <p className="text-[10px] text-muted-foreground text-center">{t("creative.loadedFromSaved")}</p>
              )}
            </div>
          </EditorSection>
        </aside>
      </div>

      <UpgradePromptDialog
        open={planUpgradeOpen}
        onOpenChange={setPlanUpgradeOpen}
        title={t("plan.limit.creativesTitle")}
        requiredPlan={requiredPlanFor("creative") === "Launch" ? "Scale" : requiredPlanFor("creative")}
        description={usageLimit("creatives") !== null ? t("plan.limit.creativesDesc", { limit: String(usageLimit("creatives")) }) : undefined}
      />
    </div>
  );
}

// ── Collapsible editor section ──
function EditorSection({
  label,
  icon,
  open,
  onToggle,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition"
      >
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {icon}
          {label}
        </span>
        <ChevronDown className={cn("w-3.5 h-3.5 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

// ── Color picker field ──
function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div className="flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-1">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded border-0 bg-transparent cursor-pointer p-0" aria-label={`${label} color`} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 text-[10px] font-mono bg-transparent outline-none uppercase w-0" />
      </div>
    </label>
  );
}
